<main id="main-content" class="main-content">
    <div class="red-warning" *ngIf="errorList.length > 0">
        <mat-icon class="red-warn-icon">warning</mat-icon>
        <div class="red-warn-text">
            <span class="red-title">Incomplete section</span>
            <span class="red-text">
                The following fields are missing details. Please complete them before proceeding to the next section:
                <ul>
                    @for (error of errorList; track error) {
                    <li>{{error | titlecase}}</li>
                    }
                </ul>
            </span>
        </div>
    </div>
    <div class="subtitle"><span class="red-normal">*</span>Indicates required field</div>
    <h2 class="form-header">{{page()}}. Summary of Analysis</h2>

    <mat-card [formGroup]="form" style="margin-bottom: 24px;">
        <div class="text-div">
            <b><span class="red">*</span>Introduction/Background</b>
            <br>
            @if (simple()) {
                <span class="gray-text">Provide a brief introduction and/or background of the fire and the reasons behind the pre-assessment funding request.</span>
            } @else {
                <span class="gray-text">Provide a brief introduction and/or background of the fire.</span>
            }
        </div>
        <!-- Rich Text input -->
        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
        <div class="NgxEditor__Wrapper">
            <ngx-editor-menu [editor]="editor1" [toolbar]="toolbar"></ngx-editor-menu>
            <ngx-editor formControlName="background" [editor]="editor1" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'background')"></ngx-editor>
        </div>
        <span class="gray-text text-count"
            [ngClass]="{ 'text-count-red': (form.controls.background.invalid && form.controls.background.touched)  }">{{form.controls.background.value?.length
            ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
        <div *ngIf="form.controls.background.invalid && form.controls.background.touched && (form.controls.background.value?.length ?? 0) > 0"
            class="editor-error">
            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
        </div>
    </mat-card>

    <mat-card [formGroup]="form" style="margin-bottom: 24px;" *ngIf="!optional() && !simple()">

        <div class="sub-title">
            Critical Values, Risks, Threats & Resources
        </div>
        <div class="border-div"></div>
        <div class="text-div" style="margin-top: 15px; margin-bottom: 5px;"><b>Critical Values and Threats - Risk
                Assessment:</b></div>
        <p style="max-width: 1000px; padding-left: 16px;">Critical Values were identified during the BAER assessment
            with potential to be at risk from post-fire
            threats. Narrative descriptions of the risk assessments are included here along with a link to the specific
            determinations as documented in the Critical Value spreadsheet. The BAER team evaluated the risk to critical
            values following the risk assessment guidance provided in Forest Service Manual 2523 and guidance papers.
        </p>
        <div class="text-div" style="margin-bottom: 5px;"><b>Critical Value Matrix</b></div>
        <mat-grid-list cols="4" rowHeight="62px" gutterSize="0">
            <mat-grid-tile colspan="1" rowspan="1" class="matrix-header">Probability of Damage or Loss</mat-grid-tile>
            <mat-grid-tile colspan="3" rowspan="1" class="matrix-header" style="margin: 0;">
                <mat-grid-list cols="3" rowHeight="30px" class="matrix-sub-table">
                    <mat-grid-tile colspan="3" rowspan="1" class="matrix-header no-border"
                        style="margin-top: 10px;">Magnitude of Consequences /
                        Risk</mat-grid-tile>
                    <mat-grid-tile colspan="1" rowspan="1" class="matrix-header no-border"
                        style="margin-top: 10px;">Major</mat-grid-tile>
                    <mat-grid-tile colspan="1" rowspan="1" class="matrix-header no-border"
                        style="margin-top: 10px;">Moderate</mat-grid-tile>
                    <mat-grid-tile colspan="1" rowspan="1" class="matrix-header no-border"
                        style="margin-top: 10px;">Minor</mat-grid-tile>
                </mat-grid-list>
            </mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1">Very Likely</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="matrix-red">Very High</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="matrix-red">Very High</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1">Low</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1">Likely</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="matrix-red">Very High</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="matrix-red">High</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1">Low</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1">Possible</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="matrix-red">High</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="matrix-yellow">Intermediate</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1">Low</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="bottom-border">Unlikely</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="bottom-border matrix-yellow">Intermediate</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="bottom-border">Low</mat-grid-tile>
            <mat-grid-tile colspan="1" rowspan="1" class="bottom-border">Very Low</mat-grid-tile>
        </mat-grid-list>
        <div class="text-div">
            <b><span class="red" *ngIf="!optional()">*</span>Critical Values Risk Matrix file link</b><span *ngIf="optional()" class="optional">(Optional)</span>
            <span class="gray-text"> Add the box link to the critical values risk matrix.</span>
        </div>
        <div class="whole-line">
            <mat-form-field appearance="outline" class="whole-field">
                <input matInput formControlName="critical_values" required aria-label="Fire or Complex Name"
                    placeholder="https://www.box.com/link">
                <mat-error>Please enter a valid URL</mat-error>
            </mat-form-field>
        </div>

        <div class="text-div" style="padding-top: 4px;">
            <b>Critical Values Highlights Summary</b>
            <br><br>
            <span class="gray-text">
                Describe Critical Values/Resources and Threats. Provide a brief narrative summary of the critical values
                (life, safety, property, natural and cultural resources) and risk ratings, providing a rationale for the
                risk determinations.
                <br><br>
                When the Burned Area Emergency Response (BAER) team determines that emergency treatments are not
                necessary, summarize the rationale and the watershed conditions which led to this conclusion.
            </span>
        </div>

        <div class="text-div">
            <b><span class="red" *ngIf="!optional()">*</span>Human Life and Safety</b><span *ngIf="optional()" class="optional">(Optional)</span>
        </div>
        <!-- Rich Text input -->
        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
        <div class="NgxEditor__Wrapper">
            <ngx-editor-menu [editor]="editor2" [toolbar]="toolbar"></ngx-editor-menu>
            <ngx-editor formControlName="threats_human" [editor]="editor2" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'threats_human')"></ngx-editor>
        </div>
        <span class="gray-text text-count"
            [ngClass]="{ 'text-count-red': (form.controls.threats_human.invalid && form.controls.threats_human.touched)  }">{{form.controls.threats_human.value?.length
            ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
        <div *ngIf="form.controls.threats_human.invalid && form.controls.threats_human.touched && (form.controls.threats_human.value?.length ?? 0) > 0"
            class="editor-error">
            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
        </div>
        <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
            [saveSubject]="saveSubject" section="4" question="human_life"></image-upload>

        <div class="text-div">
            <b><span class="red" *ngIf="!optional()">*</span>Property</b><span *ngIf="optional()" class="optional">(Optional)</span>
        </div>
        <!-- Rich Text input -->
        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
        <div class="NgxEditor__Wrapper">
            <ngx-editor-menu [editor]="editor3" [toolbar]="toolbar"></ngx-editor-menu>
            <ngx-editor formControlName="threatsproperty" [editor]="editor3" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'threatsproperty')"></ngx-editor>
        </div>
        <span class="gray-text text-count"
            [ngClass]="{ 'text-count-red': (form.controls.threatsproperty.invalid && form.controls.threatsproperty.touched)  }">{{form.controls.threatsproperty.value?.length
            ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
        <div *ngIf="form.controls.threatsproperty.invalid && form.controls.threatsproperty.touched && (form.controls.threatsproperty.value?.length ?? 0) > 0"
            class="editor-error">
            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
        </div>
        <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
            [saveSubject]="saveSubject" section="4" question="property"></image-upload>

        <div class="text-div">
            <b><span class="red" *ngIf="!optional()">*</span>Natural Resources</b><span *ngIf="optional()" class="optional">(Optional)</span>
        </div>
        <!-- Rich Text input -->
        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
        <div class="NgxEditor__Wrapper">
            <ngx-editor-menu [editor]="editor4" [toolbar]="toolbar"></ngx-editor-menu>
            <ngx-editor formControlName="threats_natural" [editor]="editor4" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'threats_natural')"></ngx-editor>
        </div>
        <span class="gray-text text-count"
            [ngClass]="{ 'text-count-red': (form.controls.threats_natural.invalid && form.controls.threats_natural.touched)  }">{{form.controls.threats_natural.value?.length
            ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
        <div *ngIf="form.controls.threats_natural.invalid && form.controls.threats_natural.touched && (form.controls.threats_natural.value?.length ?? 0) > 0"
            class="editor-error">
            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
        </div>
        <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
            [saveSubject]="saveSubject" section="4" question="natural_res"></image-upload>

        <div class="text-div">
            <b><span class="red" *ngIf="!optional()">*</span>Cultural and Heritage Resources</b><span *ngIf="optional()" class="optional">(Optional)</span>
        </div>
        <!-- Rich Text input -->
        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
        <div class="NgxEditor__Wrapper">
            <ngx-editor-menu [editor]="editor5" [toolbar]="toolbar"></ngx-editor-menu>
            <ngx-editor formControlName="threats_cultural" [editor]="editor5" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'threats_cultural')"></ngx-editor>
        </div>
        <span class="gray-text text-count"
            [ngClass]="{ 'text-count-red': (form.controls.threats_cultural.invalid && form.controls.threats_cultural.touched)  }">{{form.controls.threats_cultural.value?.length
            ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
        <div *ngIf="form.controls.threats_cultural.invalid && form.controls.threats_cultural.touched && (form.controls.threats_cultural.value?.length ?? 0) > 0"
            class="editor-error">
            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
        </div>
        <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
            [saveSubject]="saveSubject" section="4" question="cultural_res"></image-upload>

    </mat-card>

    <mat-card [formGroup]="form" style="padding-bottom: 24px;" *ngIf="!optional() && !simple()">

        <div class="text-div">
            <b id="probability-of-completing-treatment-heading">Probability of Completing Treatment Prior to Damaging Storm or Event</b>
            <br>
            <span class="gray-text">
                Probability of completion prior to the first major storm event. Estimated to the nearest 10 percent.
            </span>
        </div>
        <!-- TABLE Ownership -->
        <div class="table-container">
            <table aria-labelledby="probability-of-completing-treatment-caption probability-of-completing-treatment-heading">
                <caption id="probability-of-completing-treatment-caption" class="usa-sr-only">
                  Probability of Completing Treatment Prior to Damaging Storm or Event
                </caption>
                <tr class="table-header">
                    <th scope="col">Treatment Type</th>
                    <th scope="col" class="right">Probability</th>
                </tr>
                <tr class="table-row">
                    <td class="text-cell">Land</td>
                    <td>
                        <mat-form-field appearance="outline" class="example small-field">
                            <input class="right" formControlName="treat_prob_land" matInput aria-label="treatment land"
                                mask="00||0" suffix="%">
                        </mat-form-field>
                    </td>
                </tr>
                <tr class="table-row">
                    <td class="text-cell">Channel</td>
                    <td>
                        <mat-form-field appearance="outline" class="example small-field">
                            <input class="right" formControlName="treat_prob_channel" matInput
                                aria-label="treatment land" mask="00||0" suffix="%">
                        </mat-form-field>
                    </td>
                </tr>
                <tr class="table-row">
                    <td class="text-cell">Road Trails</td>
                    <td>
                        <mat-form-field appearance="outline" class="example small-field">
                            <input class="right" formControlName="treat_prob_trails" matInput
                                aria-label="treatment land" mask="00||0" suffix="%">
                        </mat-form-field>
                    </td>
                </tr>
                <tr class="table-row">
                    <td class="text-cell">Protection Safety</td>
                    <td>
                        <mat-form-field appearance="outline" class="example small-field">
                            <input class="right" formControlName="treat_prob_safety" matInput
                                aria-label="treatment land" mask="00||0" suffix="%">
                        </mat-form-field>
                    </td>
                </tr>
            </table>
        </div>

        <div class="text-div">
            <b><span class="red" *ngIf="!optional()">*</span>Cost of No-Action (Including Loss)</b><span *ngIf="optional()" class="optional">(Optional)</span>
            <br>
            <span class="gray-text">Enter the cost of the no-action or baseline alternative as calculated using an
                appropriate cost-benefit analysis tool.</span>
        </div>
        <!-- Rich Text input -->
        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
        <div class="NgxEditor__Wrapper">
            <ngx-editor-menu [editor]="editor6" [toolbar]="toolbar"></ngx-editor-menu>
            <ngx-editor formControlName="cost_no_action" [editor]="editor6" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'cost_no_action')"></ngx-editor>
        </div>
        <span class="gray-text text-count"
            [ngClass]="{ 'text-count-red': (form.controls.cost_no_action.invalid && form.controls.cost_no_action.touched)  }">{{form.controls.cost_no_action.value?.length
            ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
        <div *ngIf="form.controls.cost_no_action.invalid && form.controls.cost_no_action.touched && (form.controls.cost_no_action.value?.length ?? 0) > 0"
            class="editor-error">
            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
        </div>

        <div class="text-div">
            <b><span class="red" *ngIf="!optional()">*</span>Cost of Selected Alternative (Including Loss)</b><span *ngIf="optional()" class="optional">(Optional)</span>
            <br>
            <span class="gray-text">Enter the cost of the selected alternative as calculated using an appropriate
                cost-benefit analysis tool.</span>
        </div>
        <!-- Rich Text input -->
        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
        <div class="NgxEditor__Wrapper">
            <ngx-editor-menu [editor]="editor7" [toolbar]="toolbar"></ngx-editor-menu>
            <ngx-editor formControlName="cost_alternative" [editor]="editor7" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'cost_alternative')"></ngx-editor>
        </div>
        <span class="gray-text text-count"
            [ngClass]="{ 'text-count-red': (form.controls.cost_alternative.invalid && form.controls.cost_alternative.touched)  }">{{form.controls.cost_alternative.value?.length
            ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
        <div *ngIf="form.controls.cost_alternative.invalid && form.controls.cost_alternative.touched && (form.controls.cost_alternative.value?.length ?? 0) > 0"
            class="editor-error">
            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
        </div>
    </mat-card>
</main>
